import apiClient from "./apiClient";
import adminApiClient from "./adminApiClient";
import useApiWrapper from "./apiWrapper";

/** --------------------------
 *  Build Query Params
 *  ------------------------- */
const buildUrl = (baseUrl, filters = {}) => {
  const queryParams = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      // Handle boolean flag (dropdown → true)
      queryParams.append(
        key === "dropdown" ? "drop_down" : key,
        key === "dropdown" ? true : value
      );
    }
  });

  return queryParams.toString()
    ? `${baseUrl}?${queryParams.toString()}`
    : baseUrl;
};

// apiWrapper flattens DRF field errors into "An error occurred."; surface the
// first field message instead so the user knows what to fix.
const failMessage = (body) => {
  if (body?.message && body.message !== "An error occurred.") return body.message;
  const field = Object.entries(body || {}).find(([k]) => !["status", "message"].includes(k));
  if (field) {
    const [key, val] = field;
    const text = [].concat(val).filter((v) => typeof v === "string").join(" ");
    if (text) return key === "non_field_errors" ? text : `${key.replace(/_/g, " ")}: ${text}`;
  }
  return body?.message || "Something went wrong.";
};

/** --------------------------
 *  useApiMethods Hook
 *
 *  By default a failed request resolves to { status: "fail", message, ...body }
 *  rather than throwing. Pass { throwOnFail: true } to make failed writes
 *  (create/update/patch/remove) reject instead, shaped like an axios error
 *  (`e.response.data.message`), for screens that handle errors in try/catch.
 *  Reads keep resolving either way.
 *  ------------------------- */
const useApiMethods = ({ throwOnFail = false } = {}) => {
  const { apiWrapper } = useApiWrapper();

  // Common request handler
  const request = async (method, url, data = null, options = {}, setLoading, isAdmin = false) => {
    const client = isAdmin ? adminApiClient : apiClient;
    const result = await apiWrapper(() =>
      client.request({ method, url, data, ...options }),
      setLoading
    );
    if (throwOnFail && method !== "get" && result?.status === "fail") {
      const message = failMessage(result);
      const err = new Error(message);
      err.response = { data: { ...result, message } };
      throw err;
    }
    return result;
  };

  /** CRUD Methods */
  const fetch = (url, filters = {}, setLoading, isAdmin = false) => {
    const URL = buildUrl(`/${url}`, filters);
    return request("get", URL, null, {}, setLoading, isAdmin);
  };

  const create = (url, data, isFormData = false, setLoading, isAdmin = false) => {
    const headers = {
      "Content-Type": isFormData ? "multipart/form-data" : "application/json",
    };
    return request("post", url, data, { headers }, setLoading, isAdmin);
  };

  const update = (url, data, isFormData = false, setLoading, isAdmin = false) => {
    const headers = {
      "Content-Type": isFormData ? "multipart/form-data" : "application/json",
    };
    return request("put", url, data, { headers }, setLoading, isAdmin);
  };

  // Partial update. `update` sends the whole resource; this exists for the
  // cases that genuinely change one field — a status transition, say — where
  // PUT would imply the caller had the rest of the record to send.
  const patch = (url, data, setLoading, isAdmin = false) => {
    const headers = { "Content-Type": "application/json" };
    return request("patch", url, data, { headers }, setLoading, isAdmin);
  };

  const remove = (url, data, setLoading, isAdmin = false) => {
    return request("delete", url, data, {}, setLoading, isAdmin);
  };

  return { fetch, create, update, patch, remove };
};

export default useApiMethods;
